import type React from "react";
import { useTranslation } from "react-i18next";

import { copyTextToClipboard } from "_helpers/clipboard";

import { useDomainHandler } from "hooks/useDomainHandler";
import { useLocaleDateFormatter } from "hooks/useLocaleDateFormatter";

import { Heading4 } from "styles/elements/Headings";
import { ParagraphLarge } from "styles/elements/Texts";
import { Box, CssFlex, CssIcon } from "styles/helpers/layout";

import { InviteBundleStatusTag } from "../InviteBundleStatusTag/InviteBundleStatusTag";

import { buildUrlWithPathParams } from "_helpers/utils/urlBuilder";
import { Note } from "components/Note/Note";
import { MISC_PAGE_ROUTES } from "constants/routes";
import { UpdateInviteLinkUsageLimitForm } from "../UpdateInviteLinkUsageLimitForm/UpdateInviteLinkUsageLimitForm";
import { StyledInviteInput, StyledNarrowButton, StyledTipsBox } from "./InviteLinkDetails.styled";

export interface InviteLinkDetailsProps {
  invitationId: string;
  usage: { current: number; max: number };
  products: string;
  educationName: string;
  schoolName?: string;
  groupName: string | null;
  licenseExpirationDate: Date;
}

export const InviteLinkDetails: React.FC<InviteLinkDetailsProps> = ({
  invitationId,
  usage,
  products,
  educationName,
  schoolName,
  groupName,
  licenseExpirationDate,
}) => {
  const { t } = useTranslation();
  const { formatDate } = useLocaleDateFormatter();
  const { getStudentTermByDomain } = useDomainHandler();

  const inviteLinkUrl = `${window.location.origin}${buildUrlWithPathParams(
    MISC_PAGE_ROUTES.CLAIM_LINK_INVITE_LICENSE,
    { inviteLinkId: invitationId },
  )}`;

  const rows = [
    {
      label: t("modal:createLinkInviteModal.steps.linkDetails.details.status"),
      value: (
        <InviteBundleStatusTag
          dataCy="created-link-status"
          currentUsage={usage.current}
          maxUsage={usage.max}
          expirationDate={licenseExpirationDate.toISOString()}
        />
      ),
    },
    {
      label: t("modal:createLinkInviteModal.steps.linkDetails.details.maxUsageLimit"),
      value: (
        <UpdateInviteLinkUsageLimitForm
          inviteLinkId={invitationId}
          currentUsage={usage.current}
          currentMaxUsageLimit={usage.max}
        />
      ),
    },
    {
      label: t("modal:createLinkInviteModal.steps.linkDetails.details.usage"),
      value: `${usage.current} / ${usage.max}`,
    },
    {
      label: t("modal:createLinkInviteModal.steps.linkDetails.details.product"),
      value: products || "-",
    },
    {
      label: t("modal:createLinkInviteModal.steps.linkDetails.details.education"),
      value: educationName || "-",
    },
    ...(schoolName
      ? [
          {
            label: t("modal:createLinkInviteModal.steps.linkDetails.details.school"),
            value: schoolName || "-",
          },
        ]
      : []),
    ...(groupName
      ? [
          {
            label: t("modal:createLinkInviteModal.steps.linkDetails.details.group"),
            value: groupName ?? "-",
          },
        ]
      : []),
    {
      label: t("modal:createLinkInviteModal.steps.linkDetails.details.expires"),
      value: formatDate(licenseExpirationDate.toISOString()),
    },
  ];

  return (
    <CssFlex flexDirection="column" gap={1}>
      {/* -- Invite link with copy button -- */}
      {inviteLinkUrl && (
        <CssFlex flexDirection="column" gap={0.5}>
          <label htmlFor="created-invite-link-input">
            {t("modal:createLinkInviteModal.steps.linkDetails.inviteLinkLabel")}
          </label>
          <CssFlex flexDirection="row" gap={0.5} alignItems="center">
            <Box style={{ flex: 1, minWidth: 0 }}>
              <StyledInviteInput
                readOnly
                width="100%"
                dataCy="created-invite-link-input"
                id="created-invite-link-input"
                value={inviteLinkUrl}
              />
            </Box>
            <StyledNarrowButton
              variant="brand"
              dataCy="copy-invite-link-button"
              onClick={() => copyTextToClipboard(inviteLinkUrl)}
            >
              <CssFlex alignItems="center" gap={0.5}>
                <CssIcon iconName="far fa-clipboard" />
                {t("modal:createLinkInviteModal.steps.linkDetails.copyButton")}
              </CssFlex>
            </StyledNarrowButton>
          </CssFlex>
        </CssFlex>
      )}

      <Note variant="warning" dataCy="invite-link-access-warning">
        {t("teacher-dashboard:invitationManagement.linkInviteDetailsModal.accessWarning")}
      </Note>

      {/* -- Link details table -- */}
      <StyledTipsBox flexDirection="column" gap={1}>
        <Heading4>{t("modal:createLinkInviteModal.steps.linkDetails.details.title")}</Heading4>
        <CssFlex flexDirection="column" gap={1}>
          {rows.map(({ label, value }) => (
            <CssFlex
              key={label}
              flexDirection="row"
              justifyContent="space-between"
              gap={1}
              alignItems="center"
            >
              <ParagraphLarge>{label}</ParagraphLarge>
              <span data-cy={`link-detail-${label.toLowerCase()}`}>{value}</span>
            </CssFlex>
          ))}
        </CssFlex>
      </StyledTipsBox>

      {/* -- Tips for sharing -- */}
      <StyledTipsBox flexDirection="column" gap={0.5}>
        <Heading4>{t("modal:createLinkInviteModal.steps.linkDetails.tips.title")}</Heading4>
        <ul>
          <li>{t("modal:createLinkInviteModal.steps.linkDetails.tips.items.lms")}</li>
          <li>{t("modal:createLinkInviteModal.steps.linkDetails.tips.items.chat")}</li>
          <li>
            {t("modal:createLinkInviteModal.steps.linkDetails.tips.items.monitor", {
              studentTerm: getStudentTermByDomain({ usePlural: true }),
            })}
          </li>
          <li>{t("modal:createLinkInviteModal.steps.linkDetails.tips.items.expiry")}</li>
        </ul>
      </StyledTipsBox>
    </CssFlex>
  );
};
